/**
 * Self-grading (brief §exam: `/exam/:id/grade`). Per problem: the student's
 * own final answer/notes, the model solution with `solutionFigure`, rubric
 * checkboxes with points, a running total out of 18 with the G/VG lines.
 */
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { EXAM_TOTAL_POINTS, THRESHOLD_G, THRESHOLD_VG } from "@shared/exam";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { QuestionParts } from "@client/components/QuestionParts";
import { ReportIssue } from "@client/components/ReportIssue";
import { SourceBadge } from "@client/components/SourceBadge";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  PageHeader,
  ProgressBar,
  Spinner,
  useToast,
} from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatNumber, formatPoints } from "@client/lib/format";
import { useExamDetail, useGradeExam } from "./api";
import { clearGradeDraft, loadGradeDraft, saveGradeDraft } from "./gradeDraft";
import { paperLabel } from "./paper";

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function ExamGradePage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  // Review-A Important 3: both client auto-submit paths (the countdown
  // reaching zero, and discovering server-side that the deadline already
  // passed) navigate here with this router state, so the explanation shows
  // up right where the student actually lands — not only, later, on the
  // result page once they finish self-grading.
  const autoSubmitted = Boolean(
    (location.state as { autoSubmitted?: boolean } | null)?.autoSubmitted,
  );
  const detailQuery = useExamDetail(id);
  const gradeMutation = useGradeExam(id ?? "");

  // Restored from `sessionStorage` (UX-16); entries for rubric items the
  // problem no longer has are ignored when totalling and submitting.
  const [checked, setChecked] = useState<Record<string, string[]>>(
    () => (id ? loadGradeDraft(id) : null) ?? {},
  );

  const detail = detailQuery.data;

  // A `role="status"` that mounts already filled is often not spoken, and a
  // fill one commit later is no better (both land before assistive technology
  // next looks). So the notice is a plain paragraph that takes focus once the
  // page has rendered: a focus move is announced by every screen reader.
  // Gated on `detail` so it runs after the notice is in the DOM, not during
  // the spinner.
  const pageMounted = Boolean(detail);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (autoSubmitted && pageMounted) noticeRef.current?.focus();
  }, [autoSubmitted, pageMounted]);

  if (detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!detail) return <Navigate to="/exam" replace />;
  if (detail.session.status === "in_progress") return <Navigate to="/exam/session" replace />;
  // An abandoned sitting has nothing to grade or reveal.
  if (detail.session.status === "abandoned") return <Navigate to="/exam" replace />;
  if (detail.session.status === "graded")
    return <Navigate to={`/exam/${detail.session.id}/result`} replace />;
  if (!detail.revealed) return null;

  const total = round2(
    detail.revealed.reduce((sum, entry) => {
      const ticked = checked[entry.question.id] ?? [];
      const rubricTotal = entry.question.rubric
        .filter((r) => ticked.includes(r.id))
        .reduce((s, r) => s + r.points, 0);
      return (
        sum +
        Math.min(
          rubricTotal,
          entry.question.rubric.reduce((s, r) => s + r.points, 0),
        )
      );
    }, 0),
  );

  // P0-3: before the first tick the total is 0 only because nothing is graded yet, so no verdict.
  const rubricIds = new Set(
    detail.revealed.flatMap((entry) =>
      entry.question.rubric.map((r) => `${entry.question.id}:${r.id}`),
    ),
  );
  const tickedCount = detail.revealed.reduce(
    (sum, entry) =>
      sum +
      (checked[entry.question.id] ?? []).filter((rid) =>
        rubricIds.has(`${entry.question.id}:${rid}`),
      ).length,
    0,
  );
  const graded = tickedCount > 0;

  function toggle(questionId: string, rubricId: string, isChecked: boolean) {
    const current = checked[questionId] ?? [];
    const next = isChecked ? [...current, rubricId] : current.filter((id2) => id2 !== rubricId);
    const updated = { ...checked, [questionId]: next };
    setChecked(updated);
    saveGradeDraft(detail!.session.id, updated);
  }

  async function finish() {
    try {
      await gradeMutation.mutateAsync(
        Object.fromEntries(
          (detail!.revealed ?? []).map((entry) => [
            entry.question.id,
            {
              rubricChecked: (checked[entry.question.id] ?? []).filter((rubricId) =>
                entry.question.rubric.some((r) => r.id === rubricId),
              ),
            },
          ]),
        ),
      );
      clearGradeDraft(detail!.session.id);
      navigate(`/exam/${detail!.session.id}/result`);
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader
        title={t("exam.grade.title")}
        description={t("exam.grade.description")}
        eyebrow={paperLabel(detail.session, t)}
      />

      {autoSubmitted && (
        <p
          ref={noticeRef}
          tabIndex={-1}
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 outline-none dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
        >
          {t("exam.grade.autoSubmitted")}
        </p>
      )}

      <Card className="sticky top-16 z-10">
        <div className="flex items-center justify-between">
          <p className="font-display text-lg text-ink-900 dark:text-ink-50">
            {t("exam.grade.runningTotal", {
              total: formatNumber(total, lang, { maxDecimals: 1 }),
              max: formatNumber(EXAM_TOTAL_POINTS, lang),
            })}
          </p>
          <Badge
            tone={
              !graded
                ? "neutral"
                : total >= THRESHOLD_VG
                  ? "success"
                  : total >= THRESHOLD_G
                    ? "accent"
                    : "neutral"
            }
          >
            {!graded
              ? t("exam.grade.notGraded")
              : total >= THRESHOLD_VG
                ? t("exam.verdict.VG")
                : total >= THRESHOLD_G
                  ? t("exam.verdict.G")
                  : t("exam.verdict.U")}
          </Badge>
        </div>
        <ProgressBar
          className="mt-2"
          value={Math.min(1, total / EXAM_TOTAL_POINTS)}
          label={t("exam.grade.pointsLabel")}
        />
        <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
          {t("exam.grade.thresholds", {
            g: formatNumber(THRESHOLD_G, lang),
            vg: formatNumber(THRESHOLD_VG, lang),
          })}
        </p>
        <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
          {t("exam.grade.tickedCount", {
            ticked: String(tickedCount),
            count: String(rubricIds.size),
          })}
        </p>
        {!graded && (
          <p className="mt-1 text-xs text-ink-700 dark:text-ink-200">{t("exam.grade.howTo")}</p>
        )}
      </Card>

      {detail.revealed.map((entry) => {
        const answer = detail.session.answers[entry.question.id];
        const publicQuestion = detail.session.questions.find(
          (row) => row.question.id === entry.question.id,
        )?.question;
        return (
          <Card
            key={entry.slot}
            title={t("exam.session.problemTitle", {
              slot: entry.slot,
              total: detail.revealed!.length,
            })}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="neutral">
                  {formatPoints(
                    entry.question.parts.reduce((s, p) => s + p.points, 0),
                    lang,
                  )}
                </Badge>
                <SourceBadge source={publicQuestion?.source} />
              </div>
            }
          >
            <Localized value={entry.question.statement} />
            {entry.question.given.length > 0 && (
              <div className="mt-4 space-y-2 rounded-lg bg-ink-100 p-3 text-sm dark:bg-ink-800/60">
                {entry.question.given.map((item, i) => (
                  <Localized key={i} value={item} variant="compact" />
                ))}
              </div>
            )}
            {/* Part-tagged figures are drawn under their part by `QuestionParts`. */}
            {entry.question.figures
              .filter((figure) => !figure.partId)
              .map((figure, i) => (
                <Figure key={i} figure={figure} />
              ))}
            <QuestionParts parts={entry.question.parts} figures={entry.question.figures} />

            <div className="mt-4 rounded-lg border border-ink-200 p-3 text-sm dark:border-ink-800">
              <h3 className="mb-1 text-xs font-medium uppercase text-ink-500 dark:text-ink-400">
                {t("exam.grade.yourAnswer")}
              </h3>
              <p className="text-ink-800 dark:text-ink-100">{answer?.final || "—"}</p>
              {answer?.notes && (
                <>
                  <h3 className="mb-1 mt-3 text-xs font-medium uppercase text-ink-500 dark:text-ink-400">
                    {t("exam.grade.yourNotes")}
                  </h3>
                  <p className="whitespace-pre-wrap text-ink-700 dark:text-ink-200">
                    {answer.notes}
                  </p>
                </>
              )}
            </div>

            <div className="mt-4 border-t border-ink-200 pt-4 dark:border-ink-800">
              <h3 className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
                {t("exam.grade.modelSolution")}
              </h3>
              <Localized value={entry.question.solution} />
              {entry.question.solutionFigure && <Figure figure={entry.question.solutionFigure} />}
              {entry.question.finalAnswer && (
                <div className="mt-2 font-medium text-ink-800 dark:text-ink-100">
                  <Localized value={entry.question.finalAnswer} variant="inline" />
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <h3 className="text-sm font-medium text-ink-700 dark:text-ink-200">
                {t("exam.grade.rubricTitle")}
              </h3>
              {entry.question.rubric.map((item) => (
                <Checkbox
                  key={item.id}
                  label={
                    <span>
                      <Localized value={item.text} variant="inline" /> (
                      {formatPoints(item.points, lang)})
                    </span>
                  }
                  checked={(checked[entry.question.id] ?? []).includes(item.id)}
                  onChange={(event) => toggle(entry.question.id, item.id, event.target.checked)}
                />
              ))}
            </div>

            <ReportIssue questionId={entry.question.id} className="mt-4" />
          </Card>
        );
      })}

      <Button onClick={() => void finish()} loading={gradeMutation.isPending} fullWidth>
        {t("exam.grade.done")}
      </Button>
    </div>
  );
}

export default ExamGradePage;
